import React, { useEffect, useState } from 'react';
import Paper from '@mui/material/Paper';
import MenuList from '@mui/material/MenuList';
import MenuItem from '@mui/material/MenuItem';
import ListItemText from '@mui/material/ListItemText';
import ListItemIcon from '@mui/material/ListItemIcon';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import ExitToAppOutlinedIcon from '@mui/icons-material/ExitToAppOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import PaymentIcon from '@mui/icons-material/Payment';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import { useNavigate, useLocation } from 'react-router-dom';

interface CommonLayoutProps {
    onLogout: () => void;
    userRole: string
}

export const Navbar: React.FC<CommonLayoutProps> = ({ onLogout, userRole }) => {

    const location = useLocation();
    const navigate = useNavigate();

    const [menuList, setMenuList] = useState<any[]>([]);
    const [lastSelectedID, setLastSelectedID] = useState<string>("5");
    const [isLoggingOut, setIsLoggingOut] = useState(false);

    function navigateOn(id: string, href: string) {
        setLastSelectedID(id);
        navigate(href);
    }

    function createMenuItem(
        id: string,
        label: string,
        href: string,
        icon: any,
        action: any = null
    ) {
        return { id, label, href, icon, action };
    }

    /*
    const bottomMenuList = [
        //createMenuItem('13', 'Log out', '/logout', <GroupIcon fontSize="small" />, onLogout),
    ];
    */

    useEffect(() => {
        console.log('🔍 [NAVBAR] Checking sessionStorage for user...');
        const userString = sessionStorage.getItem('user');
        
        if(userString === null){ 
            console.log('❌ [NAVBAR] No user in sessionStorage - menu will be empty');
            return;
        }

        let user = JSON.parse(userString);
        console.log('👤 [NAVBAR] User data:', user);
        console.log('🔑 [NAVBAR] UserRole prop:', userRole);
        
        // Base menu items for all users
        const baseMenu = [
            createMenuItem('5', 'Dashboard', '/dashboard', <DashboardOutlinedIcon fontSize="medium" sx={{ color: '#8B6F47' }} />),
            createMenuItem('100', 'Gestione Pratiche', '/works', <WorkOutlineIcon fontSize="medium" sx={{ color: '#8B6F47' }} />),
            createMenuItem('6', 'Fascicolo Elettronico', '/documents', <Inventory2OutlinedIcon fontSize="medium" sx={{ color: '#8B6F47' }} />),
        ];
        
        // Add Admin Dashboard only if user is Admin
        if (userRole === 'Admin') {
            console.log('✅ [NAVBAR] User is Admin - adding Admin Dashboard menu');
            baseMenu.push(
                createMenuItem('200', 'Admin Dashboard', '/admin-dashboard', <AdminPanelSettingsIcon fontSize="medium" sx={{ color: '#8B6F47' }} />),
                createMenuItem('201', 'Gestione Pagamenti', '/admin-payments', <PaymentIcon fontSize="medium" sx={{ color: '#8B6F47' }} />),
                createMenuItem('203', 'Supporto', '/admin-support', <SupportAgentIcon fontSize="medium" sx={{ color: '#8B6F47' }} />)
                ,createMenuItem('204', 'Gestione notifiche', '/admin-notifications', <SupportAgentIcon fontSize="medium" sx={{ color: '#8B6F47' }} />)
            );
        }
        
        setMenuList(baseMenu);
    }, [userRole]);
    
    // Aggiorna la selezione quando cambia la route
    useEffect(() => {
        const currentPath = location.pathname;
        // Gestisce anche sotto-percorsi (es: /work-detail, /create-work sotto /works)
        const selectedItem = menuList.find(item => {
            if (item.href === '/') return false;
            // Match esatto o sotto-percorso (es: /works, /work-detail, /create-work)
            if (item.href === '/works') {
                return currentPath.startsWith('/work') || currentPath === '/works';
            }
            return currentPath.startsWith(item.href);
        });
        if (selectedItem) {
            setLastSelectedID(selectedItem.id);
        }
    }, [location.pathname, menuList]);

    return (
        <>
        <Paper 
            elevation={0} 
            sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                justifyContent: 'space-between',
                height: '100%',
                width: '240px',
                flexShrink: 0,
                bgcolor: '#FAFAFA',
                borderRight: '1px solid #E0E0E0',
                overflowY: 'auto'
            }}
        >
            <MenuList disablePadding={true} sx={{ pt: 2 }}>
                {menuList.map(single =>
                    <MenuItem 
                        key={single.id} 
                        selected={lastSelectedID === single.id} 
                        disabled={single.href === "/"}
                        onClick={e => navigateOn(single.id, single.href)}
                        sx={{ 
                            height: "70px",
                            mx: 2,
                            my: 0.5,
                            borderRadius: 2,
                            transition: 'all 0.3s ease',
                            '&:hover': {
                                bgcolor: '#F5F5F5',
                                transform: 'translateX(4px)',
                            },
                            '&.Mui-selected': {
                                bgcolor: '#8B6F47',
                                color: '#FFFFFF',
                                boxShadow: '0 4px 12px rgba(139, 111, 71, 0.4)',
                                '&:hover': {
                                    bgcolor: '#6B5635',
                                    transform: 'translateX(4px)',
                                },
                                '& .MuiListItemIcon-root': {
                                    color: '#FFFFFF !important',
                                },
                                '& .MuiListItemText-primary': {
                                    fontWeight: 600,
                                    color: '#FFFFFF',
                                }
                            }
                        }}
                    >
                        <ListItemIcon sx={{ 
                            minWidth: 50,
                            color: lastSelectedID === single.id ? '#FFFFFF !important' : '#8B6F47',
                            transition: 'color 0.3s ease',
                            '& svg': {
                                color: lastSelectedID === single.id ? '#FFFFFF !important' : '#8B6F47',
                            }
                        }}>
                            {single.icon}
                        </ListItemIcon>
                        <ListItemText 
                            primary={single.label}
                            sx={{
                                '& .MuiListItemText-primary': {
                                    fontSize: '0.95rem',
                                    color: lastSelectedID === single.id ? 'white' : '#424242',
                                    transition: 'all 0.3s ease'
                                }
                            }}
                        />
                    </MenuItem>)
                }
            </MenuList>

            <MenuList disablePadding={true} sx={{ pb: 2 }}>
                {/* bottomMenuList rimosso - lasciamo solo Esci */}
                <MenuItem 
                    onClick={async (e) => {
                        setIsLoggingOut(true);
                        try {
                            await onLogout();
                        } finally {
                            setIsLoggingOut(false);
                        }
                    }} 
                    key="menu-logout"
                    sx={{ 
                        color: "#757575", 
                        height: "60px",
                        mx: 2,
                        my: 0.5,
                        borderRadius: 2,
                        transition: 'all 0.3s ease',
                        '&:hover': {
                            bgcolor: '#FFEBEE',
                            color: '#D32F2F',
                            transform: 'translateX(4px)',
                            '& .MuiListItemIcon-root': {
                                color: '#D32F2F',
                            }
                        }
                    }}
                >
                    <ListItemIcon sx={{ 
                        minWidth: 50,
                        color: '#757575',
                        transition: 'color 0.3s ease'
                    }}>
                        <ExitToAppOutlinedIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText 
                        primary="Esci"
                        sx={{
                            '& .MuiListItemText-primary': {
                                fontSize: '0.95rem',
                            }
                        }}
                    />
                </MenuItem>
            </MenuList>
        </Paper>
        <Backdrop
            sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.modal + 1 }}
            open={isLoggingOut}
        >
            <CircularProgress color="inherit" />
        </Backdrop>
        </>
    );
}
